import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/hooks/use-toast';
import { authService, notificationService } from '@/api';
import { setToken, removeToken } from '@/lib/utils';
import { getFCMToken } from '@/lib/firebase';
import config from '@/config/env';

// Định nghĩa các interface
interface User {
  id: number;
  username: string;
  email: string;
  full_name: string;
  phone: string;
  role: string;
  status: string;
  created_at: string;
  updated_at: string;
  avatar_url: string;
  email_verified: boolean;
  phone_verified: boolean;
  last_login: string;
}

interface LoginCredentials {
  username: string;
  password: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

export interface AuthResult {
  success: boolean;
  message?: string;
  user?: User;
}

/**
 * Hook quản lý xác thực người dùng
 */
const useAuth = () => {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null
  });
  
  const navigate = useNavigate();

  // Khởi tạo state từ localStorage
  useEffect(() => {
    const initAuth = async () => {
      const userStr = localStorage.getItem('user');
      
      if (userStr) {
        try {
          const user = JSON.parse(userStr) as User;
          setState({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null
          });
        } catch (error) {
          console.error('Failed to parse user data from localStorage:', error);
          localStorage.removeItem('user');
          setState({ ...state, isLoading: false });
        }
      } else {
        setState({ ...state, isLoading: false });
      }
    };

    initAuth();
  }, []);

  /**
   * Đăng nhập người dùng
   * @param credentials Thông tin đăng nhập
   */
  const login = useCallback(async (credentials: LoginCredentials): Promise<AuthResult> => {
    setState({ ...state, isLoading: true, error: null });
    
    try {
      // Gọi API đăng nhập
      const response = await authService.login(credentials);
      
      if (response.success && response.data) {
        const userData = response.data.user;
        
        // Kiểm tra role hợp lệ
        const allowedRoles = ['admin', 'manager', 'staff'];
        
        if (!allowedRoles.includes(userData.role)) {
          setState({
            ...state,
            isLoading: false,
            error: 'Tài khoản không có quyền truy cập hệ thống'
          });
          
          toast({
            title: 'Đăng nhập thất bại',
            description: 'Tài khoản không có quyền truy cập hệ thống',
            variant: 'destructive',
          });
          
          return {
            success: false,
            message: 'Tài khoản không có quyền truy cập hệ thống'
          };
        }
        
        // Lưu thông tin người dùng
        localStorage.setItem('user', JSON.stringify(userData));
        
        setState({
          user: userData,
          isAuthenticated: true,
          isLoading: false,
          error: null
        });
        
        toast({
          title: 'Đăng nhập thành công',
          description: `Xin chào, ${userData.full_name || userData.username}!`,
        });

        // Đăng ký Firebase token nếu có
        registerFirebaseToken(userData.id);
        
        return {
          success: true,
          message: 'Đăng nhập thành công',
          user: userData
        };
      }
      
      setState({
        ...state,
        isLoading: false,
        error: response.message || 'Đăng nhập thất bại'
      });
      
      toast({
        title: 'Đăng nhập thất bại',
        description: response.message || 'Tên đăng nhập hoặc mật khẩu không chính xác',
        variant: 'destructive',
      });
      
      return {
        success: false,
        message: response.message || 'Đăng nhập thất bại'
      };
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Đã xảy ra lỗi khi đăng nhập';
      
      setState({
        ...state,
        isLoading: false,
        error: errorMessage
      });
      
      toast({
        title: 'Đăng nhập thất bại',
        description: errorMessage,
        variant: 'destructive',
      });
      
      return {
        success: false,
        message: errorMessage
      };
    }
  }, [state]);

  /**
   * Đăng nhập DEV mode (chỉ dùng trong môi trường phát triển)
   */
  const devLogin = useCallback(async (): Promise<AuthResult> => {
    setState({ ...state, isLoading: true, error: null });
    
    try {
      // Tạo một người dùng giả lập
      const mockUser: User = {
        id: 1,
        username: 'dev_user',
        email: 'dev@example.com',
        full_name: 'Người dùng DEV',
        phone: '0123456789',
        role: 'admin',
        status: 'active',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        avatar_url: '',
        email_verified: true,
        phone_verified: true,
        last_login: new Date().toISOString()
      };
      
      // Tạo token giả lập (chỉ để frontend hoạt động)
      const mockToken = 'dev_mock_token_' + Math.random().toString(36).substring(2);
      const mockRefreshToken = 'dev_mock_refresh_token_' + Math.random().toString(36).substring(2);
      
      // Lưu thông tin xác thực
      setToken(mockToken, mockRefreshToken);
      localStorage.setItem('user', JSON.stringify(mockUser));
      
      setState({
        user: mockUser,
        isAuthenticated: true,
        isLoading: false,
        error: null
      });
      
      toast({
        title: 'Đã kích hoạt chế độ DEV',
        description: 'Đăng nhập với tài khoản DEV thành công',
      });
      
      return {
        success: true,
        message: 'Đăng nhập DEV thành công',
        user: mockUser
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Đã xảy ra lỗi khi đăng nhập DEV';
      
      setState({
        ...state,
        isLoading: false,
        error: errorMessage
      });
      
      toast({
        title: 'Đăng nhập thất bại',
        description: errorMessage,
        variant: 'destructive',
      });
      
      return {
        success: false,
        message: errorMessage
      };
    }
  }, [state]);

  /**
   * Đăng ký Firebase token sau khi đăng nhập
   */
  const registerFirebaseToken = useCallback(async (userId: number) => {
    try {
      const token = getFCMToken();
      if (!token) return;
      
      // Kiểm tra xem token đã gửi trước đó chưa
      const tokenSent = localStorage.getItem('fcmTokenSent') === 'true' &&
                       localStorage.getItem('fcmTokenSaved') === token;
      
      if (!tokenSent) {
        console.log('Registering Firebase token after login...');
        
        // Chuẩn bị thông tin thiết bị
        const userAgent = navigator.userAgent.toLowerCase();
        const deviceType = /android/i.test(userAgent) ? 'android' : 
                           /iphone|ipad|ipod/i.test(userAgent) ? 'ios' : 'web';
        
        const deviceId = localStorage.getItem('device_id') || 
                         'web_' + Math.random().toString(36).substring(2, 15);
        
        if (!localStorage.getItem('device_id')) {
          localStorage.setItem('device_id', deviceId);
        }
        
        // Gửi token đến server
        await notificationService.registerToken({
          token,
          device_type: deviceType,
          device_id: deviceId,
          app_version: '1.0.0',
          platform_version: `${navigator.platform} - ${navigator.userAgent}`
        });
        
        console.log('Firebase token registered successfully');
      }
    } catch (error) {
      console.error('Failed to register Firebase token:', error);
    }
  }, []);

  /**
   * Đăng xuất người dùng
   */
  const logout = useCallback(async (): Promise<boolean> => {
    try {
      // Xóa dữ liệu xác thực trước
      removeToken();
      localStorage.removeItem('user');
      
      // Cập nhật state
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      });
      
      // Thực hiện gọi API đăng xuất sau khi đã xóa dữ liệu
      try {
        await authService.logout();
        console.log('Logout API called');
      } catch (apiError) {
        console.error('Error calling logout API:', apiError);
        // Tiếp tục quá trình đăng xuất ngay cả khi API thất bại
      }
      
      return true;
    } catch (error) {
      console.error('Logout failed:', error);
      
      // Vẫn xóa dữ liệu xác thực ngay cả khi có lỗi
      try {
        removeToken();
        localStorage.removeItem('user');
      } catch (cleanupError) {
        console.error('Error during logout cleanup:', cleanupError);
      }
      
      return false;
    }
  }, []);

  return {
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    error: state.error,
    login,
    logout,
    registerFirebaseToken,
    devLogin,
    isDevelopment: config.debug
  };
};

export default useAuth; 